import React, { useState, useEffect } from 'react';
import { PurchaseHistory } from 'ai-api/types/purchase';
import PurchaseService from '../../Data/PurchaseService';
import {
    PurchaseHistoryList,
    PurchaseDetailsModal
} from '../../Components/Purchase';
import './index.scss';
import './HistoryFilters.scss';
import { useSession } from '@/Recoil/Session/Session';
import { tLoader } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';

const translations = {
    en: {
        "Search by teacher or product name...": "Search by teacher or product name...",
        "You must be logged in to view your purchase history": "You must be logged in to view your purchase history",
        "Failed to load purchase history": "Failed to load purchase history",
        "An error occurred while fetching purchase history": "An error occurred while fetching purchase history"
    },
    id: {
        "Search by teacher or product name...": "Cari berdasarkan nama guru atau produk...",
        "You must be logged in to view your purchase history": "Anda harus masuk untuk melihat riwayat pembelian Anda",
        "Failed to load purchase history": "Gagal memuat riwayat pembelian",
        "An error occurred while fetching purchase history": "Terjadi kesalahan saat mengambil riwayat pembelian"
    },
    ko: {
        "Search by teacher or product name...": "선생님 또는 상품명으로 검색...",
        "You must be logged in to view your purchase history": "구매 내역을 보려면 로그인해야 합니다",
        "Failed to load purchase history": "구매 내역을 불러오지 못했습니다",
        "An error occurred while fetching purchase history": "구매 내역을 가져오는 중 오류가 발생했습니다"
    },
    vn: {
        "Search by teacher or product name...": "Tìm kiếm theo tên giáo viên hoặc sản phẩm...",
        "You must be logged in to view your purchase history": "Bạn phải đăng nhập để xem lịch sử mua hàng",
        "Failed to load purchase history": "Không thể tải lịch sử mua hàng",
        "An error occurred while fetching purchase history": "Đã xảy ra lỗi khi tải lịch sử mua hàng"
    }
};

const PurchaseHistoryPage: React.FC = () => {
    const [, forceUpdate] = useState({});
    const t = tLoader(translations);

    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);

    const { session } = useSession();
    const [purchases, setPurchases] = useState<PurchaseHistory[]>([]);
    const [filteredPurchases, setFilteredPurchases] = useState<PurchaseHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPurchase, setSelectedPurchase] = useState<PurchaseHistory | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    // Fetch purchase history
    useEffect(() => {
        const fetchPurchaseHistory = async () => {
            setLoading(true);
            setError(null);

            try {
                if (!session.email) {
                    throw new Error(t('You must be logged in to view your purchase history'));
                }
                const response = await PurchaseService.getPurchaseHistory('product');

                if (response.code === 200 && response.data) {
                    setPurchases(response.data);
                    setFilteredPurchases(response.data);
                } else {
                    setError(t('Failed to load purchase history'));
                }
            } catch (err) {
                console.error('Error fetching purchase history:', err);
                setError((err as Error).message || t('An error occurred while fetching purchase history'));
            } finally {
                setLoading(false);
            }
        };

        fetchPurchaseHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session]);

    // Filter purchases when search query changes
    useEffect(() => {
        let filtered = [...purchases];

        // Filter by search query
        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.productName.toLowerCase().includes(query)
            );
        }

        // Sort by created_at (most recent first)
        filtered.sort((a, b) => b.created_at - a.created_at);

        setFilteredPurchases(filtered);
    }, [purchases, searchQuery]);

    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    const handleViewDetails = (purchase: PurchaseHistory) => {
        setSelectedPurchase(purchase);
        setShowDetailsModal(true);
    };

    const closeDetailsModal = () => {
        setShowDetailsModal(false);
    };

    return (
        <div className="purchase-history-page">
            <div className="page-content">
                <div className="purchase-history-filters">
                    <div className="filters-search">
                        <input
                            type="text"
                            placeholder={t("Search by teacher or product name...")}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="search-input"
                        />
                    </div>
                </div>

                <PurchaseHistoryList
                    purchases={filteredPurchases}
                    onViewDetails={handleViewDetails}
                    loading={loading}
                    error={error}
                />
            </div>

            <PurchaseDetailsModal
                isOpen={showDetailsModal}
                onClose={closeDetailsModal}
                purchase={selectedPurchase}
            />
        </div>
    );
};

export default PurchaseHistoryPage;
